import { format, intervalToDuration, isPast, isSameDay, isTomorrow } from 'date-fns'
import { TFunction } from 'i18next'

import { formatPrice } from '@/utils'

import { VISITOR_PARKING_FREE_URL_KEY } from './visitor-parking.constants'

export const formatRelativeParkingEndTime = (
  endDate: Date | number | undefined,
  t: TFunction<['visitor-parking']>,
): string => {
  if (!endDate) return ''

  const timeFormat = 'h:mm a'

  if (isSameDay(new Date(), new Date(endDate))) {
    return format(endDate, timeFormat)
  }

  if (isTomorrow(new Date(endDate))) {
    return t('visitor-parking:time.tomorrowAt', { time: format(endDate, timeFormat) })
  }

  return format(new Date(endDate), `MMM d, ${timeFormat}`)
}

export const formatParkingEndTime = (endDate?: Date | number): string => {
  if (!endDate) return ''
  return format(new Date(endDate), 'MMM d, h:mm a')
}

export const formatDuration = (duration: number, t: TFunction<['visitor-parking']>): string => {
  return t('visitor-parking:duration.hours', { count: duration })
}

export const formatCurrency = (amount?: number): string => formatPrice(amount, { zeroPrice: true })

export const getVisitorParkingSessionUrl = (
  ticketNumber: string,
  token: string,
  isFree?: boolean,
): string => {
  const freeParam = isFree ? `&${VISITOR_PARKING_FREE_URL_KEY}=true` : ''
  return `/visitor-parking?ticketNumber=${ticketNumber}&token=${token}${freeParam}`
}

export const getRelativePaymentSuccessReturnUrl = (
  ticketNumber: string,
  token: string,
  isFree?: boolean,
): string => {
  const freeParam = isFree ? `&${VISITOR_PARKING_FREE_URL_KEY}=true` : ''
  return `/visitor-parking/payment-success?ticketNumber=${ticketNumber}&token=${token}${freeParam}`
}

export const getFullPaymentSuccessReturnUrl = (
  ticketNumber: string,
  token: string,
  isFree?: boolean,
): string => {
  return `${window.location.origin}/#${getRelativePaymentSuccessReturnUrl(ticketNumber, token, isFree)}`
}

export const getRemainingParkingTime = (
  endDate: Date | number,
): { hours: number; minutes: number; seconds: number } => {
  const now = new Date()

  if (isPast(new Date(endDate))) {
    return { hours: 0, minutes: 0, seconds: 0 }
  }

  const duration = intervalToDuration({
    end: new Date(endDate),
    start: now,
  })

  return {
    hours: duration.hours ?? 0,
    minutes: duration.minutes ?? 0,
    seconds: duration.seconds ?? 0,
  }
}

export const formatRemainingTimeUnit = (value: number): string => {
  return value.toString().padStart(2, '0')
}
